import React, { useState } from 'react';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import {
  UserCircle, Type, Palette, Layers, Activity, Box, Sparkles, Image as ImageIcon,
  ChevronDown, ChevronRight, Sliders,
} from 'lucide-react';
import ProfilePreview from '../ProfilePreview';
import { clampBadgeText } from '../utils/badges';
import { getTextureOverlay } from '../core/identity/textures';
import {
  ProfileFont, NameStyle, Palette as PaletteType, TextureCategory, Texture,
  AvatarDecoration, AuraMode, ShowcaseBadge, ProfileAnimation,
  LFOWaveform, ColorFilter, ProfileCustom, DEFAULT_CUSTOMIZATION
} from '../core/identity/ProfileTypes';

export interface TextureMeta { id: Texture; label: string; category: TextureCategory; }
export const TEXTURE_META: TextureMeta[] = [
  { id: "Static", label: "Signal Noise", category: "Tech" },
  { id: "Scanlines", label: "CRT Scanlines", category: "Tech" },
  { id: "Glitch Bars", label: "Glitch Bars", category: "Tech" },
  { id: "VCR Static", label: "VCR Static", category: "Tech" },
  { id: "Hex Grid", label: "Hex Grid", category: "Tech" },
  { id: "Psychedelic Wash", label: "Acid Wash", category: "Psychedelic" },
  { id: "Liquid Chrome", label: "Liquid Chrome", category: "Psychedelic" },
  { id: "Fractal Zoom", label: "Fractal Zoom", category: "Psychedelic" },
  { id: "Chromatic Aberration", label: "Chromatic Aberration", category: "Psychedelic" },
  { id: "Matrix Rain", label: "Matrix Rain", category: "Tech" },
  { id: "Fairy Dust", label: "Fairy Dust", category: "Mystical" },
  { id: "Ethereal Mist", label: "Ethereal Mist", category: "Mystical" },
  { id: "Arcane Runes", label: "Arcane Runes", category: "Mystical" },
  { id: "Runic Grid", label: "Runic Grid", category: "Mystical" },
  { id: "Starfield", label: "Starfield", category: "Mystical" },
  { id: "Kawaii Hearts", label: "Floating Hearts", category: "Kawaii" },
  { id: "Cherry Blossom", label: "Cherry Blossom", category: "Kawaii" },
  { id: "Pastel Sparkles", label: "Pastel Sparkles", category: "Kawaii" },
  { id: "Bubble Pop", label: "Bubble Pop", category: "Kawaii" },
  { id: "Smoke Plumes", label: "Smoke Plumes", category: "Stoner" },
  { id: "Lava Lamp", label: "Lava Lamp", category: "Stoner" },
  { id: "Rasta Weave", label: "Rasta Gradient", category: "Stoner" }
];

export interface CallsignFontGroup { group: string; options: { value: ProfileFont; label: string }[]; }
export const CALLSIGN_FONT_GROUPS: CallsignFontGroup[] = [
  {
    group: 'Futuristic & Electronic',
    options: [
      { value: 'Inter', label: 'Inter (Standard)' },
      { value: 'Rajdhani', label: 'Rajdhani' },
      { value: 'Orbitron', label: 'Orbitron' },
      { value: 'VT323', label: 'VT323' },
      { value: 'Retro', label: 'Space Mono' },
      { value: 'Arcade', label: 'Press Start 2P' },
      { value: 'Pixel', label: 'Silkscreen' },
      { value: 'Horror', label: 'Oxanium' },
      { value: 'Terminal', label: 'JetBrains Mono' },
      { value: 'Audiowide', label: 'Audiowide' },
      { value: 'Chakra Petch', label: 'Chakra Petch' },
    ],
  },
  {
    group: 'Mythical & Ethereal',
    options: [
      { value: 'Ethereal', label: 'Ethereal Roman' },
      { value: 'Ornate Imperial', label: 'Ornate Imperial' },
      { value: 'Elven Script', label: 'Elven Script' },
      { value: 'Mythic Gothic', label: 'Mythic Gothic' },
      { value: 'Aura Round', label: 'Cosmic Aura' },
      { value: 'Runic Scroll', label: 'Ancient Scroll' },
      { value: 'Uncial Antiqua', label: 'Uncial Antiqua' },
      { value: 'Metamorphous', label: 'Metamorphous' },
    ],
  },
];
const renderFontOptionGroups = () => CALLSIGN_FONT_GROUPS.map(g => (
  <optgroup key={g.group} label={g.group} className="bg-slate-900 text-white/50">
    {g.options.map(o => (
      <option key={o.value} value={o.value} className="bg-slate-900 text-white">{o.label}</option>
    ))}
  </optgroup>
));

export interface AvatarFrameMeta { id: AvatarDecoration; label: string; category: TextureCategory; }
export const AVATAR_FRAME_META: AvatarFrameMeta[] = [
  { id: "None", label: "None", category: "Tech" },
  { id: "Neon Frame", label: "Neon", category: "Tech" },
  { id: "Cyber Glitch", label: "Cyber Glitch", category: "Tech" },
  { id: "Neural Circuit", label: "Neural Circuit", category: "Tech" },
  { id: "Quantum Halo", label: "Quantum Halo", category: "Tech" },
  { id: "Pulsing Aura", label: "Pulsing Aura", category: "Psychedelic" },
  { id: "Rainbow Ring", label: "Rainbow Ring", category: "Psychedelic" },
  { id: "Halo", label: "Halo", category: "Mystical" },
  { id: "Elven Vines", label: "Elven Vines", category: "Mystical" },
  { id: "Dragon Scales", label: "Dragon Scales", category: "Mystical" },
  { id: "Celestial Orbit", label: "Celestial Orbit", category: "Mystical" },
  { id: "Runic Ring", label: "Runic Ring", category: "Mystical" },
  { id: "Angelic Wings", label: "Angelic Wings", category: "Mystical" },
  { id: "Cat Ears", label: "Cat Ears", category: "Kawaii" },
  { id: "Flower Crown", label: "Flower Crown", category: "Kawaii" },
];

export const TEXTURE_CATEGORIES: TextureCategory[] = ["Tech", "Mystical", "Psychedelic", "Kawaii", "Stoner"];

export const AURA_MODE_META: { id: AuraMode; label: string; description: string }[] = [
  { id: "None", label: "No Aura", description: "Disable glow ring." },
  { id: "Static", label: "Static Ambient", description: "Constant glow." },
  { id: "Pulse Animated", label: "Pulsing Glow", description: "Steady 2s pulse." },
  { id: "Breathe", label: "Breathing Glow", description: "Slow 4s breath cycle." },
  { id: "RGB Color-Cycle", label: "RGB Color-Cycle", description: "Cycles through rainbow." },
  { id: "Audio-Reactive", label: "Audio Reactive", description: "Pulses with synthetic amplitude." },
  { id: "Chaotic", label: "Chaotic Glitch", description: "Random rapid flashing." },
];

export const LFO_WAVEFORM_META: { id: LFOWaveform; label: string; description: string }[] = [
  { id: "None", label: "Off", description: "No modulation." },
  { id: "Sine", label: "Sinewave", description: "Smooth oscillation." },
  { id: "Sawtooth", label: "Sawtooth", description: "Linear ramp, abrupt reset." },
  { id: "Pulse", label: "Square / Pulse", description: "On/off 50% duty cycle." },
  { id: "S&H", label: "Sample & Hold", description: "Random jumps at LFO rate." },
];

export const COLOR_FILTER_META: { id: ColorFilter; label: string; description: string }[] = [
  { id: "None", label: "Off", description: "No color transformation." },
  { id: "HSV Inversion", label: "HSV Inversion", description: "Flip hue 180°." },
  { id: "Channel Shift", label: "Channel Shift", description: "RGB channel offset ramp." },
  { id: "Psychodynamic Red", label: "Psychodynamic Red", description: "Pulsing red filter overlay." },
];

interface ProfileCustomizerProps {
  draftProfile: any;
  setDraftProfile: (profile: any) => void;
  category: string;
  targetType?: string;
}

/** Single source for the union of accordion section ids. */
type SectionId =
  | 'identity' | 'typography' | 'backdrops' | 'textures'
  | 'modulation' | 'frames' | 'auras' | 'banner';

interface AccordionItem {
  id: SectionId;
  title: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}

const ACCORDION: AccordionItem[] = [
  { id: 'identity',    title: 'Identity Core',         icon: UserCircle },
  { id: 'typography',  title: 'Typography & Style',    icon: Type       },
  { id: 'backdrops',   title: 'Backdrops & Palettes',  icon: Palette    },
  { id: 'textures',    title: 'Textures & Overlays',   icon: Layers     },
  { id: 'modulation',  title: 'Color Modulation & LFO',icon: Activity   },
  { id: 'frames',      title: 'Avatar Frames & Badges',icon: Box        },
  { id: 'auras',       title: 'Auras & Animations',    icon: Sparkles   },
  { id: 'banner',      title: 'Banner Core',           icon: ImageIcon  },
];

// ===================== Reusable sub-components =====================
//
// Hoisted to module scope (previously defined inside ProfileCustomizer's
// render body). A component defined inside a parent's render function is
// a NEW component type on every render, which makes React unmount and
// remount its DOM nodes on every parent re-render instead of patching
// them in place. For ColorChip's `<input type="color">` specifically,
// that meant the element could be torn down mid-interaction with the
// browser's native color picker — clicking inside the saturation/
// brightness palette appeared to do nothing because the input itself
// kept getting replaced under the picker. Hoisting gives every one of
// these components a stable identity across renders.

const SectionHeader: React.FC<{ id: SectionId; icon: AccordionItem['icon']; title: string; open: boolean; onToggle: (id: SectionId) => void }> = ({ id, icon: Icon, title, open, onToggle }) => (
  <button
    type="button"
    onClick={() => onToggle(open ? 'identity' : id)}
    className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-xl bg-white/5 border border-white/5 hover:bg-white/[0.07] transition-colors"
  >
    <span className="flex items-center gap-2.5 text-[10px] font-black uppercase tracking-[0.2em] text-white/70">
      <Icon size={14} className="text-[var(--color-cyan)] shrink-0" />
      {title}
    </span>
    {open
      ? <ChevronDown size={14} className="text-white/40 shrink-0" />
      : <ChevronRight size={14} className="text-white/40 shrink-0" />}
  </button>
);

const Panel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mt-1.5 space-y-2.5 bg-black/25 p-3 rounded-xl border border-white/5 shadow-inner">
    {children}
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="space-y-1">
    <label className="text-white/50 text-[8px] font-black uppercase tracking-widest ml-1 block">{label}</label>
    {children}
  </div>
);

const Slider: React.FC<{
  label: string; suffix?: string;
  value: number; min: number; max: number; step?: number;
  onChange: (v: number) => void;
  accent?: string;
}> = ({ label, suffix, value, min, max, step = 1, onChange, accent = 'var(--color-cyan)' }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between">
      <label className="text-white/50 text-[8px] font-black uppercase tracking-widest ml-1">{label}</label>
      <span className="text-[8px] font-mono text-white/40">{value}{suffix ?? ''}</span>
    </div>
    <input
      type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full accent-[var(--color-cyan)] opacity-70 hover:opacity-100 transition-opacity"
      style={{ accentColor: accent }}
    />
  </div>
);

const ColorChip: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => (
  <Field label={label}>
    <div className="flex items-center justify-between gap-1.5 bg-black/30 p-2 rounded-lg border border-white/5">
      <div className="min-w-0 flex-1">
        <div className="text-[8px] font-mono text-white/40 uppercase">{value}</div>
      </div>
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-6 h-6 rounded bg-transparent cursor-pointer border border-white/20 p-0 shrink-0" />
    </div>
  </Field>
);

const Select: React.FC<{ label: string; value: string; onChange: (v: string) => void; children: React.ReactNode }> = ({ label, value, onChange, children }) => (
  <Field label={label}>
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full neural-glass border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white outline-none bg-[#090d16] appearance-none">
      {children}
    </select>
  </Field>
);

/** Texture tile preview - tiny square that uses the same recipe
 *  background as the full overlay so users pick visually. We strip
 *  `mixBlendMode` here because blend modes apply to adjacent tile
 *  siblings inside the grid, which makes screen/multiply recipes look
 *  indistinct. The real preview in ProfilePreview keeps the blend. */
const TextureTile: React.FC<{ id: Texture; selected: boolean; onPick: () => void }> = ({ id, selected, onPick }) => {
  const recipe = getTextureOverlay(id);
  const isBorderless = id === 'None';
  // For "None" we render transparent (so the picker shows an empty cell).
  // For tiles without a background recipe we fall back to dark panel.
  const bg = isBorderless ? undefined : (recipe?.background ?? '#1a1330');
  return (
    <button
      type="button"
      onClick={onPick}
      title={id}
      className={`group relative aspect-square rounded-lg overflow-hidden border-2 transition-all
        ${selected ? 'border-[var(--color-cyan)] shadow-[0_0_10px_var(--color-cyan)]' : 'border-white/10 hover:border-white/30'}
        ${isBorderless ? 'bg-black/30' : ''}`}
      style={{
        background: bg,
        backgroundSize: recipe?.backgroundSize,
      }}
    >
      {selected && (
        <div className="absolute inset-0 ring-2 ring-inset ring-[var(--color-cyan)]" />
      )}
    </button>
  );
};

/** Avatar frame tile - simple square with theme-color border preview. */
const FrameTile: React.FC<{ id: AvatarDecoration; selected: boolean; onPick: () => void; tc1: string; tc2: string }> = ({ id, selected, onPick, tc1, tc2 }) => {
  const isNone = id === 'None';
  // Per-frame hint rendering using emoji-style composition that matches
  // a tiny version of what ProfilePreview would render.
  const preview = (() => {
    switch (id) {
      case 'None': return null;
      case 'Cat Ears': return <>🐱🐱</>;
      case 'Halo': return <div className="w-3 h-1 rounded-full bg-yellow-300 shadow-[0_0_6px_#fde68a]" />;
      case 'Flower Crown': return <div className="flex gap-0.5">🌸🌼🌷</div>;
      case 'Pulsing Aura': return <div className="w-3 h-3 rounded-full" style={{ background: `radial-gradient(circle, ${tc1}, transparent)`, boxShadow: `0 0 6px ${tc1}` }} />;
      case 'Rainbow Ring': return <div className="w-3 h-3 rounded-full" style={{ background: 'conic-gradient(#ff0080,#ffd700,#10ff80,#10ffd0,#a040ff,#ff0080)', padding: 1 }} />;
      case 'Dragon Scales': return <div className="text-[8px]">🐉</div>;
      default: return <div className="w-3 h-3 rounded-full border-2" style={{ borderColor: tc1, boxShadow: `0 0 4px ${tc1}` }} />;
    }
  })();
  return (
    <button
      type="button"
      onClick={onPick}
      title={id}
      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all flex items-center justify-center text-[8px]
        ${selected ? 'border-[var(--color-cyan)] shadow-[0_0_10px_var(--color-cyan)]' : 'border-white/10 hover:border-white/30'}
        ${isNone ? 'bg-black/30' : 'bg-black/40'}`}
    >
      {preview}
    </button>
  );
};

const PickGrid: React.FC<{
  pick: Texture;
  onPick: (v: Texture) => void;
}> = ({ pick, onPick }) => (
  <div className="space-y-2.5">
    {TEXTURE_CATEGORIES.map(cat => {
      const items = TEXTURE_META.filter(t => t.category === cat);
      return (
        <div key={cat}>
          <div className="text-[8px] font-black uppercase tracking-widest text-white/40 mb-1.5 ml-1">
            {cat}
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
            {items.map(item => (
              <TextureTile key={item.id} id={item.id} selected={pick === item.id} onPick={() => onPick(item.id)} />
            ))}
          </div>
        </div>
      );
    })}
  </div>
);

const FrameGrid: React.FC<{
  pick: AvatarDecoration;
  onPick: (v: AvatarDecoration) => void;
  tc1: string;
  tc2: string;
}> = ({ pick, onPick, tc1, tc2 }) => (
  <div className="space-y-2.5">
    {TEXTURE_CATEGORIES.map(cat => {
      const items = AVATAR_FRAME_META.filter(t => t.category === cat);
      if (items.length === 0) return null;
      return (
        <div key={cat}>
          <div className="text-[8px] font-black uppercase tracking-widest text-white/40 mb-1.5 ml-1">
            {cat}
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5">
            {items.map(item => (
              <FrameTile key={item.id} id={item.id} selected={pick === item.id} onPick={() => onPick(item.id)} tc1={tc1} tc2={tc2} />
            ))}
          </div>
        </div>
      );
    })}
  </div>
);

const AuraGrid: React.FC<{ pick: AuraMode; onPick: (v: AuraMode) => void }> = ({ pick, onPick }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
    {AURA_MODE_META.map(a => (
      <button
        key={a.id}
        type="button"
        onClick={() => onPick(a.id)}
        className={`p-2.5 rounded-lg border-2 text-left transition-all ${
          pick === a.id
            ? 'border-[var(--color-cyan)] bg-[var(--color-cyan)]/10 shadow-[0_0_10px_var(--color-cyan)]'
            : 'border-white/10 bg-black/30 hover:border-white/30'
        }`}
      >
        <div className="text-[9px] font-black uppercase tracking-wider text-white">{a.label}</div>
        <div className="text-[8px] text-white/40 leading-tight mt-0.5">{a.description}</div>
      </button>
    ))}
  </div>
);

// ===================== ProfileCustomizer =====================

export default function ProfileCustomizer({ draftProfile, setDraftProfile, category, targetType }: ProfileCustomizerProps) {
  const customization: ProfileCustom = {
    ...DEFAULT_CUSTOMIZATION,
    ...(draftProfile.customization || {})
  };

  const [badgePickerOpen, setBadgePickerOpen] = useState(false);
  const [openSection, setOpenSection] = useState<SectionId>('identity');

  const updateCustomization = (patch: Partial<ProfileCustom>) => {
    setDraftProfile((prev: any) => ({
      ...prev,
      customization: { ...customization, ...patch }
    }));
  };

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setDraftProfile((prev: any) => ({ ...prev, avatar: e.target?.result as string }));
      reader.readAsDataURL(file);
    }
  };

  const handleBannerUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setDraftProfile((prev: any) => ({ ...prev, bannerImage: e.target?.result as string }));
      reader.readAsDataURL(file);
    }
  };

  // ===================== Render =====================

  const tc1 = customization.themeColor1 || customization.color1 || '#818cf8';
  const tc2 = customization.themeColor2 || customization.color2 || '#22d3ee';

  return (
    <div className="space-y-8 pb-10">
      {category === 'Profile' && (
        <div className="space-y-6 bg-white/5 p-6 rounded-[2rem] border border-white/5 shadow-xl">
          <label className="text-white/30 text-[10px] font-black uppercase tracking-[0.2em] font-space">Core Identity</label>
          <div className="space-y-4">
            <Field label="Callsign (Display Name)">
              <input type="text" value={draftProfile.name}
                onChange={(e) => setDraftProfile((prev: any) => ({ ...prev, name: e.target.value }))}
                className="w-full neural-glass-input rounded-2xl px-5 py-4 text-sm text-white outline-none focus:neural-border-glow transition-all"
                placeholder="Username / Display Name" />
            </Field>
            <Field label="Pronouns">
              <input type="text" value={customization.pronouns || ''}
                onChange={(e) => updateCustomization({ pronouns: e.target.value })}
                className="w-full neural-glass-input rounded-2xl px-5 py-4 text-sm text-white outline-none focus:neural-border-glow transition-all"
                placeholder="they/them, he/him, she/her" />
            </Field>
            <Field label="Data Stream (Bio)">
              <textarea value={draftProfile.bio || ''}
                onChange={(e) => setDraftProfile((prev: any) => ({ ...prev, bio: e.target.value }))}
                className="w-full neural-glass-input rounded-2xl px-5 py-4 text-sm text-white h-24 outline-none focus:neural-border-glow transition-all resize-none"
                placeholder="Tell the frontier about yourself..." />
            </Field>
            <Field label="Custom Status Message">
              <input type="text" value={customization.statusMessage || ''}
                onChange={(e) => updateCustomization({ statusMessage: e.target.value })}
                className="w-full neural-glass-input rounded-2xl px-5 py-4 text-sm text-white outline-none focus:neural-border-glow transition-all"
                placeholder="Stealth mode active..." />
            </Field>
            <Field label="Custom Status">
              <input type="text" value={customization.customStatus || ''}
                onChange={(e) => updateCustomization({ customStatus: e.target.value })}
                className="w-full neural-glass-input rounded-2xl px-5 py-4 text-sm text-white outline-none focus:neural-border-glow transition-all"
                placeholder="Set your custom status..." />
            </Field>
          </div>

          <div className="pt-2">
            <input type="file" id="avatar-upload-customizer" className="hidden" onChange={handleAvatarUpload} accept="image/*" />
            <button onClick={() => document.getElementById('avatar-upload-customizer')?.click()}
              className="neural-glass w-full py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest text-white/60 hover:text-white hover:neural-border-glow transition-all flex items-center justify-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-cyan)] shadow-[0_0_8px_var(--color-cyan)]" />
              Re-Initialize Avatar
            </button>

            {draftProfile.avatar && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-1">
                  <label className="text-[9px] text-white/50 uppercase tracking-widest">Pan Avatar X</label>
                  <input type="range" min="0" max="100" 
                    value={parseInt((customization.avatarPosition || '50% 50%').split(' ')[0])} 
                    onChange={(e) => {
                      const pos = `${e.target.value}% ${parseInt((customization.avatarPosition || '50% 50%').split(' ')[1]) || 50}%`;
                      updateCustomization({ avatarPosition: pos });
                    }} 
                    className="w-full accent-[var(--neon)]" 
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] text-white/50 uppercase tracking-widest">Pan Avatar Y</label>
                  <input type="range" min="0" max="100" 
                    value={parseInt((customization.avatarPosition || '50% 50%').split(' ')[1]) || 50} 
                    onChange={(e) => {
                      const pos = `${parseInt((customization.avatarPosition || '50% 50%').split(' ')[0])}% ${e.target.value}%`;
                      updateCustomization({ avatarPosition: pos });
                    }} 
                    className="w-full accent-[var(--neon)]" 
                  />
                </div>
              </div>
            )}

            <div className="border-t border-white/5 my-5" />

            <label className="text-white/30 text-[10px] font-black uppercase tracking-[0.2em] font-space block mb-3">Banner</label>
            <input type="file" id="banner-upload-profile" className="hidden" onChange={handleBannerUpload} accept="image/*" />
            <button onClick={() => document.getElementById('banner-upload-profile')?.click()}
              className="neural-glass w-full py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest text-white/60 hover:text-white hover:neural-border-glow transition-all flex items-center justify-center gap-3 mb-3">
              <div className="w-1.5 h-1.5 rounded-full bg-[var(--accent-highlight)] shadow-[0_0_8px_var(--accent-highlight)]" />
              {draftProfile.bannerImage ? 'Change Banner Image' : 'Upload Banner Image'}
            </button>
            <button onClick={() => setDraftProfile((prev: any) => ({ ...prev, bannerType: 'image' }))}
              className="neural-glass w-full py-3 rounded-xl text-[9px] font-black uppercase tracking-widest text-white/40 hover:text-white/70 transition-all">
              Or customize banner type & color in Appearance → Banner Core
            </button>
          </div>
        </div>
      )}

      {category === 'Appearance' && (
        <div className="text-xs">
          {targetType && targetType !== 'user' ? (
            <Panel>
              <div className="text-[9px] font-black uppercase tracking-[0.2em] text-white/40 mb-2">Typography & Style</div>
              <div className="grid grid-cols-2 gap-2.5">
                <Select label="Callsign Font" value={customization.font} onChange={(v) => updateCustomization({ font: v as ProfileFont })}>
                  {renderFontOptionGroups()}
                </Select>
                <Select label="Callsign Art Effect" value={customization.nameStyle} onChange={(v) => updateCustomization({ nameStyle: v as NameStyle })}>
                  <option value="Solid" className="bg-slate-900 text-white">Solid White</option>
                  <option value="Gradient" className="bg-slate-900 text-white">Chroma Gradient</option>
                  <option value="Neon" className="bg-slate-900 text-white">Emissive Neon Glow</option>
                  <option value="POP" className="bg-slate-900 text-white">POP Badge</option>
                </Select>
              </div>
              {(customization.nameStyle === 'Gradient' || customization.nameStyle === 'Neon') && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <ColorChip label="Color Node A" value={customization.color1} onChange={(v) => updateCustomization({ color1: v })} />
                  <ColorChip label="Color Node B" value={customization.color2} onChange={(v) => updateCustomization({ color2: v })} />
                </div>
              )}
              {customization.nameStyle === 'Gradient' && (
                <div className="mt-2 space-y-2">
                  <Select label="Gradient Direction" value={customization.gradientDirection || '90deg'} onChange={(v) => updateCustomization({ gradientDirection: v as any })}>
                    <option value="90deg" className="bg-slate-900 text-white">Left to Right</option>
                    <option value="180deg" className="bg-slate-900 text-white">Top to Bottom</option>
                    <option value="270deg" className="bg-slate-900 text-white">Right to Left</option>
                    <option value="0deg" className="bg-slate-900 text-white">Bottom to Top</option>
                    <option value="135deg" className="bg-slate-900 text-white">Top-Left to Bottom-Right</option>
                    <option value="45deg" className="bg-slate-900 text-white">Bottom-Left to Top-Right</option>
                  </Select>
                  <button
                    type="button"
                    onClick={() => updateCustomization({ gradientAnimated: !customization.gradientAnimated })}
                    className={`w-full py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                      customization.gradientAnimated
                        ? 'bg-[var(--color-cyan)]/15 border border-[var(--color-cyan)] text-[var(--color-cyan)]'
                        : 'bg-black/30 border border-white/10 text-white/40 hover:border-white/30'
                    }`}
                  >
                    {customization.gradientAnimated ? 'Gradient Sweep: On (hover to preview)' : 'Gradient Sweep: Off'}
                  </button>
                </div>
              )}
            </Panel>
          ) : (
            <div className="space-y-2.5">
              {ACCORDION.filter(s => s.id !== 'identity').map(item => (
                <div key={item.id}>
                  <SectionHeader id={item.id} icon={item.icon} title={item.title} open={openSection === item.id} onToggle={setOpenSection} />
                  {openSection === item.id && (
                    <Panel>

                      {item.id === 'typography' && (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                            <Select label="Callsign Font" value={customization.font} onChange={(v) => updateCustomization({ font: v as ProfileFont })}>
                              {renderFontOptionGroups()}
                            </Select>
                            <Select label="Profile Body Font" value={customization.bodyFont || 'Inter'} onChange={(v) => updateCustomization({ bodyFont: v as ProfileFont })}>
                              {renderFontOptionGroups()}
                            </Select>
                            <Select label="Callsign Art Effect" value={customization.nameStyle} onChange={(v) => updateCustomization({ nameStyle: v as NameStyle })}>
                              <option value="Solid" className="bg-slate-900 text-white">Solid White</option>
                              <option value="Gradient" className="bg-slate-900 text-white">Chroma Gradient</option>
                              <option value="Neon" className="bg-slate-900 text-white">Emissive Neon Glow</option>
                              <option value="POP" className="bg-slate-900 text-white">POP Badge</option>
                            </Select>
                          </div>

                          {customization.nameStyle === 'Gradient' && (
                            <div className="mt-2 mb-4 space-y-2">
                              <Select label="Gradient Direction" value={customization.gradientDirection || '90deg'} onChange={(v) => updateCustomization({ gradientDirection: v as any })}>
                                <option value="90deg" className="bg-slate-900 text-white">Left to Right</option>
                                <option value="180deg" className="bg-slate-900 text-white">Top to Bottom</option>
                                <option value="270deg" className="bg-slate-900 text-white">Right to Left</option>
                                <option value="0deg" className="bg-slate-900 text-white">Bottom to Top</option>
                                <option value="135deg" className="bg-slate-900 text-white">Top-Left to Bottom-Right</option>
                                <option value="45deg" className="bg-slate-900 text-white">Bottom-Left to Top-Right</option>
                              </Select>
                              <button
                                type="button"
                                onClick={() => updateCustomization({ gradientAnimated: !customization.gradientAnimated })}
                                className={`w-full py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                                  customization.gradientAnimated
                                    ? 'bg-[var(--color-cyan)]/15 border border-[var(--color-cyan)] text-[var(--color-cyan)]'
                                    : 'bg-black/30 border border-white/10 text-white/40 hover:border-white/30'
                                }`}
                              >
                                {customization.gradientAnimated ? 'Gradient Sweep: On (hover to preview)' : 'Gradient Sweep: Off'}
                              </button>
                            </div>
                          )}

                          <div className="text-[9px] font-black uppercase tracking-widest text-white/40 mt-1">Effect Colors</div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <ColorChip label="Text Node A" value={customization.color1} onChange={(v) => updateCustomization({ color1: v })} />
                            <ColorChip label="Text Node B" value={customization.color2} onChange={(v) => updateCustomization({ color2: v })} />
                            <ColorChip label="Panel Node A" value={customization.themeColor1 || customization.color1} onChange={(v) => updateCustomization({ themeColor1: v })} />
                            <ColorChip label="Panel Node B" value={customization.themeColor2 || customization.color2} onChange={(v) => updateCustomization({ themeColor2: v })} />
                          </div>
                        </>
                      )}

                      {item.id === 'backdrops' && (
                        <>
                          <Select label="Profile Panel Palette" value={customization.palette} onChange={(v) => updateCustomization({ palette: v as PaletteType })}>
                            <optgroup label="Futuristic & Electronic" className="bg-slate-900 text-white/50">
                              <option value="Cyberpunk" className="bg-slate-900 text-white">Neon Cyberpunk</option>
                              <option value="Vaporwave" className="bg-slate-900 text-white">Synthwave / Vaporwave</option>
                              <option value="Outrun" className="bg-slate-900 text-white">Retro Outrun</option>
                              <option value="Neon Nights" className="bg-slate-900 text-white">Neon Nights</option>
                              <option value="Midnight" className="bg-slate-900 text-white">Deep Midnight</option>
                            </optgroup>
                            <optgroup label="Mythical & Ethereal" className="bg-slate-900 text-white/50">
                              <option value="Enchanted Forest" className="bg-slate-900 text-white">Enchanted Forest</option>
                              <option value="Celestial Stardust" className="bg-slate-900 text-white">Celestial Stardust</option>
                              <option value="Ethereal Light" className="bg-slate-900 text-white">Ethereal Light</option>
                              <option value="Fairy Grove" className="bg-slate-900 text-white">Fairy Grove</option>
                              <option value="Abyssal Deep" className="bg-slate-900 text-white">Abyssal Deep</option>
                            </optgroup>
                            <optgroup label="User Configured" className="bg-slate-900 text-white/50">
                              <option value="Custom Solid" className="bg-slate-900 text-white">Custom Solid Color</option>
                              <option value="Custom Gradient" className="bg-slate-900 text-white">Custom Gradient Theme</option>
                            </optgroup>
                          </Select>
                          {customization.palette === 'Custom Solid' && (
                            <ColorChip label="Custom Background Color" value={customization.customBgColor || '#090d16'} onChange={(v) => updateCustomization({ customBgColor: v })} />
                          )}
                        </>
                      )}

                      {item.id === 'textures' && (
                        <>
                          <div className="text-[9px] font-black uppercase tracking-widest text-white/40">CRT & Signal Texture Picker</div>
                          <PickGrid pick={customization.texture} onPick={(v) => updateCustomization({ texture: v as Texture })} />
                          <div className="grid grid-cols-2 gap-2">
                            <Slider label="Texture Opacity" suffix="%"
                              value={customization.textureOpacity ?? 45} min={0} max={100} step={1}
                              onChange={(v) => updateCustomization({ textureOpacity: v })} />
                            <Slider label="Texture Intensity" suffix=""
                              value={customization.textureIntensity ?? 50} min={0} max={100} step={1}
                              onChange={(v) => updateCustomization({ textureIntensity: v })} />
                          </div>
                          <div className="text-[9px] font-black uppercase tracking-widest text-white/40 flex items-center gap-1.5 mt-1">
                            <Sliders size={10} /> Texture Intensity + the LFO Rate slider (Color Modulation & LFO tab) together drive animation speed.
                          </div>
                          <button
                            type="button"
                            onClick={() => updateCustomization({ textureNeonSweep: !customization.textureNeonSweep })}
                            className={`w-full mt-2 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                              customization.textureNeonSweep
                                ? 'bg-[var(--color-cyan)]/15 border border-[var(--color-cyan)] text-[var(--color-cyan)]'
                                : 'bg-black/30 border border-white/10 text-white/40 hover:border-white/30'
                            }`}
                          >
                            {customization.textureNeonSweep ? 'Neon Light Sweep: On' : 'Neon Light Sweep: Off'}
                          </button>
                        </>
                      )}

                      {item.id === 'modulation' && (
                        <>
                          <div className="text-[9px] font-black uppercase tracking-widest text-white/40">LFO Waveform</div>
                          <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
                            {LFO_WAVEFORM_META.map(w => (
                              <button
                                key={w.id}
                                type="button"
                                onClick={() => updateCustomization({ lfoWaveform: w.id })}
                                className={`p-2 rounded-lg border-2 text-left transition-all ${
                                  customization.lfoWaveform === w.id
                                    ? 'border-[var(--color-cyan)] bg-[var(--color-cyan)]/10'
                                    : 'border-white/10 bg-black/30 hover:border-white/30'
                                }`}
                              >
                                <div className="text-[9px] font-black uppercase tracking-wider text-white">{w.label}</div>
                                <div className="text-[8px] text-white/40 leading-tight mt-0.5">{w.description}</div>
                              </button>
                            ))}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Slider label="LFO Rate" suffix=" Hz"
                              value={customization.lfoRate ?? 1.0} min={0.1} max={4} step={0.1}
                              onChange={(v) => updateCustomization({ lfoRate: v })} />
                            <Slider label="LFO Frequency" suffix="x"
                              value={customization.lfoFrequency ?? 1.0} min={0.1} max={4} step={0.1}
                              onChange={(v) => updateCustomization({ lfoFrequency: v })} />
                          </div>
                          <div className="text-[9px] font-black uppercase tracking-widest text-white/40">Color Filter</div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                            {COLOR_FILTER_META.map(c => (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => updateCustomization({ colorFilter: c.id })}
                                className={`p-2 rounded-lg border-2 text-left transition-all ${
                                  customization.colorFilter === c.id
                                    ? 'border-[var(--color-cyan)] bg-[var(--color-cyan)]/10'
                                    : 'border-white/10 bg-black/30 hover:border-white/30'
                                }`}
                              >
                                <div className="text-[9px] font-black uppercase tracking-wider text-white">{c.label}</div>
                                <div className="text-[8px] text-white/40 leading-tight mt-0.5">{c.description}</div>
                              </button>
                            ))}
                          </div>
                        </>
                      )}

                      {item.id === 'frames' && (
                        <>
                          <div className="text-[9px] font-black uppercase tracking-widest text-white/40">Avatar Decoration Frame</div>
                          <FrameGrid
                            pick={customization.avatarDecoration}
                            onPick={(v) => updateCustomization({ avatarDecoration: v as AvatarDecoration })}
                            tc1={tc1}
                            tc2={tc2}
                          />
                          <div className="text-[9px] font-black uppercase tracking-widest text-white/40">Showcase Badge</div>
                          <div className="flex items-stretch gap-1.5 min-w-0">
                            <button
                              type="button"
                              onClick={() => setBadgePickerOpen((v) => !v)}
                              className={`w-9 shrink-0 rounded-lg border text-base flex items-center justify-center transition-all ${
                                customization.customBadgeIcon
                                  ? 'bg-white/10 border-white/20 hover:border-[var(--color-cyan)]/50'
                                  : 'bg-white/5 border-dashed border-white/10 hover:border-[var(--color-cyan)]/50'
                              }`}
                              title={customization.customBadgeIcon ? 'Change emoji' : 'Pick emoji'}
                            >
                              {customization.customBadgeIcon || <span className="text-white/30 text-xs">＋</span>}
                            </button>
                            <input
                              type="text"
                              maxLength={customization.customBadgeIcon ? 6 : 7}
                              value={customization.customBadgeText || ''}
                              onChange={(e) => updateCustomization({
                                customBadgeText: clampBadgeText(e.target.value, !!customization.customBadgeIcon),
                              })}
                              placeholder={customization.customBadgeIcon ? '6 chars max' : '7 chars max'}
                              className="flex-1 min-w-0 neural-glass border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white outline-none bg-[#090d16]"
                            />
                          </div>
                          {badgePickerOpen && (
                            <div className="rounded-xl border border-white/10 bg-[#090d16]/90 overflow-hidden z-50 relative">
                              <div className="flex justify-between items-center px-3 py-1.5 border-b border-white/5">
                                <span className="text-[8px] font-bold text-white/40 uppercase tracking-widest">Pick emoji</span>
                                <button type="button" onClick={() => setBadgePickerOpen(false)}
                                  className="text-[8px] font-bold text-white/40 uppercase tracking-widest hover:text-white">
                                  Close
                                </button>
                              </div>
                              <EmojiPicker
                                onEmojiClick={(d: EmojiClickData) => {
                                  updateCustomization({
                                    customBadgeIcon: d.emoji,
                                    customBadgeText: clampBadgeText(customization.customBadgeText || '', true),
                                  });
                                  setBadgePickerOpen(false);
                                }}
                                width="100%" height={350}
                                searchPlaceholder="Search emoji…"
                                previewConfig={{ showPreview: false }}
                                skinTonesDisabled
                              />
                            </div>
                          )}
                        </>
                      )}

                      {item.id === 'auras' && (
                        <>
                          <Select label="Card Animation Effect" value={customization.profileAnimation || 'Glitch'} onChange={(v) => updateCustomization({ profileAnimation: v as ProfileAnimation })}>
                            <option value="None" className="bg-slate-900 text-white">None</option>
                            <option value="Glitch" className="bg-slate-900 text-white">⚡ Neural Glitch</option>
                            <option value="Cyber Surge" className="bg-slate-900 text-white">🔥 Cyber Surge</option>
                            <option value="Neon Overload" className="bg-slate-900 text-white">💜 Neon Overload</option>
                            <option value="Void Tear" className="bg-slate-900 text-white">🌀 Void Tear</option>
                            <option value="Data Corrupt" className="bg-slate-900 text-white">💀 Data Corrupt</option>
                            <option value="Plasma Burn" className="bg-slate-900 text-white">🔶 Plasma Burn</option>
                            <option value="Hologram Flicker" className="bg-slate-900 text-white">👻 Hologram Flicker</option>
                            <option value="Liquid Chrome" className="bg-slate-900 text-white">💧 Liquid Chrome</option>
                            <option value="VHS Nightmare" className="bg-slate-900 text-white">📼 VHS Nightmare</option>
                            <option value="Spectral Bloom" className="bg-slate-900 text-white">✨ Spectral Bloom</option>
                            <option value="Ethereal Aura" className="bg-slate-900 text-white">🌟 Ethereal Aura</option>
                          </Select>

                          <div className="text-[9px] font-black uppercase tracking-widest text-white/40">Card Glow Aura</div>
                          <AuraGrid pick={customization.auraMode} onPick={(v) => updateCustomization({ auraMode: v })} />

                          {customization.auraMode !== 'None' && (
                            <>
                              <div className="grid grid-cols-2 gap-2">
                                <ColorChip label="Aura Color Node A" value={customization.auraColor1} onChange={(v) => updateCustomization({ auraColor1: v })} />
                                <ColorChip label="Aura Color Node B" value={customization.auraColor2} onChange={(v) => updateCustomization({ auraColor2: v })} />
                              </div>

                              {customization.auraMode === 'Audio-Reactive' && (
                                <Slider label="Audio Gain" suffix="x"
                                  value={customization.audioGain ?? 1.0} min={0.3} max={2.0} step={0.05}
                                  onChange={(v) => updateCustomization({ audioGain: v })} />
                              )}
                              {customization.auraMode === 'RGB Color-Cycle' && (
                                <Slider label="RGB Cycle Speed" suffix="x"
                                  value={customization.rgbCycleSpeed ?? 1.0} min={0.25} max={4} step={0.05}
                                  onChange={(v) => updateCustomization({ rgbCycleSpeed: v })} />
                              )}
                              {customization.auraMode === 'Chaotic' && (
                                <Slider label="Chaotic Speed" suffix="x"
                                  value={customization.chaoticSpeed ?? 1.0} min={0.5} max={3} step={0.05}
                                  onChange={(v) => updateCustomization({ chaoticSpeed: v })} />
                              )}

                              <label className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-wider text-white/60 mt-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={!!customization.auraAffectsBanner}
                                  onChange={(e) => updateCustomization({ auraAffectsBanner: e.target.checked })}
                                  className="w-3 h-3 accent-[var(--color-cyan)]"
                                />
                                Overlay aura on banner area too
                              </label>
                            </>
                          )}
                        </>
                      )}

                      {item.id === 'banner' && (
                        <>
                          <Select label="Banner Type" value={draftProfile.bannerType || 'color'} onChange={(v) => setDraftProfile((prev: any) => ({ ...prev, bannerType: v as 'color' | 'gradient' | 'image' }))}>
                            <option value="color" className="bg-slate-900 text-white">Solid Color</option>
                            <option value="gradient" className="bg-slate-900 text-white">Gradient Overlay</option>
                            <option value="image" className="bg-slate-900 text-white">Custom Matrix Image</option>
                          </Select>
                          {draftProfile.bannerType === 'color' && (
                            <ColorChip label="Banner Color" value={draftProfile.bannerValue || '#1e1e1e'} onChange={(v) => setDraftProfile((prev: any) => ({ ...prev, bannerValue: v }))} />
                          )}
                          {draftProfile.bannerType === 'gradient' && (
                            <div className="grid grid-cols-2 gap-2 bg-black/30 p-2.5 rounded-lg border border-white/5">
                              <Slider label="Node A" suffix="°"
                                value={draftProfile.gradientHue1 ?? 260} min={0} max={360} step={1}
                                onChange={(v) => setDraftProfile((prev: any) => ({ ...prev, gradientHue1: v }))}
                                accent="var(--color-cyan)" />
                              <Slider label="Node B" suffix="°"
                                value={draftProfile.gradientHue2 ?? 320} min={0} max={360} step={1}
                                onChange={(v) => setDraftProfile((prev: any) => ({ ...prev, gradientHue2: v }))}
                                accent="var(--accent-highlight)" />
                            </div>
                          )}
                          {draftProfile.bannerType === 'image' && (
                            <div className="flex flex-col gap-2">
                              <input type="file" id="banner-upload-customizer" className="hidden" onChange={handleBannerUpload} accept="image/*" />
                              <button onClick={() => document.getElementById('banner-upload-customizer')?.click()}
                                className="neural-glass px-3 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest text-white/60 hover:text-white transition-all border-dashed border-white/20">
                                Upload Background Data
                              </button>
                            </div>
                          )}
                        </>
                      )}

                    </Panel>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
